import {ChangeDetectionStrategy, Component, ElementRef, HostListener, Input, OnInit, signal} from '@angular/core';
import {createPopper, Instance as PopperInstance} from '@popperjs/core';
import {L10nTranslationService} from 'angular-l10n';
import {EMPTY, from, Observable, Subject, switchMap, timer} from 'rxjs';
import {catchError, exhaustMap, takeUntil, tap} from 'rxjs/operators';
import {WindowRefService} from '../../services/window-ref.service';
import {destroy$} from '../destroyable';
import {CommonFlashMessageNotification} from '../flash-message/common-flash-message-notification';

const MSG_COPIED = 'components.copy.copy_success_long';

@Component({
    selector:        'copy-tooltip-balloon',
    templateUrl:     './copy-tooltip-balloon.component.html',
    styleUrls:       ['./copy-tooltip-balloon.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class.-success]': 'isCopySuccess()'
    },
    standalone:      false
})
export class CopyTooltipBalloonComponent implements OnInit {

    @Input()
    public content: string;

    @Input()
    public referenceElementRef: ElementRef;

    @Input()
    public bubbleNotification$?: Subject<CommonFlashMessageNotification>;

    @Input()
    public clearHideTimeout: () => void;

    @Input()
    public scheduleHideCopyTooltip: () => void;

    public readonly isCopySuccess = signal<boolean>(false);

    public readonly destroy$ = destroy$();

    private readonly copy$ = new Subject<string>();

    private popper: PopperInstance;

    constructor(
        private translationService: L10nTranslationService,
        private windowRefService: WindowRefService,
        public elementRef: ElementRef
    ) {}

    @HostListener('mouseenter')
    public onMouseEnter(): void {
        this.clearHideTimeout?.();
    }

    @HostListener('mouseleave')
    public onMouseLeave(): void {
        this.scheduleHideCopyTooltip?.();
    }

    public onCopyClick(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();

        this.copy$.next(this.content);
    }

    public ngOnInit(): void {
        this.listenToCopy();
        this.initPopper();
    }

    protected initPopper(): void {
        if (this.referenceElementRef) {
            this.popper = createPopper(
                this.referenceElementRef.nativeElement,
                this.elementRef.nativeElement,
                {
                    placement: 'right'
                }
            );

            this.popper.update();

            this.destroy$.subscribe(() => this.popper.destroy());
        }
    }

    private listenToCopy(): void {
        this.copy$.pipe(
            exhaustMap(contentToCopy => this.writeToClipboard(contentToCopy)),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private writeToClipboard(contentToCopy: string): Observable<unknown | never> {
        return contentToCopy && contentToCopy.length
            ? from(this.windowRefService.nativeWindow.navigator.clipboard.writeText(contentToCopy)).pipe(
                tap(() => this.notifyCopied()),
                switchMap(() => {
                    this.isCopySuccess.set(true);

                    return timer(2000).pipe(
                        tap(() => this.isCopySuccess.set(false))
                    );
                }),
                catchError(error => {
                    console.error('Could not copy text: ', error);

                    return EMPTY;
                })
            )
            : EMPTY;
    }

    private notifyCopied(): void {
        if (this.bubbleNotification$) {
            const translatedMsg = this.translationService.translate(MSG_COPIED);

            this.bubbleNotification$.next({
                message: translatedMsg
            });
        }
    }

}
